/**
 * Reconciliación · Canal `Actualización de suscripciones` v1.
 *
 * Subida del archivo generado por Control ("Descargar actualización de
 * suscripciones"). Comparación literal, cero derivaciones. Aprobación
 * granular o masiva por columna. Aplicación transaccional por fila.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { UploadCloud, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { saasApi, errText, descargarInformeCarga } from './api';
import { useDescargas } from './useDescarga';
import { PasosCarga } from './PasosCarga';

// Iter52.63 · P1 · Pasos del canal de actualización.
const PASOS_ACTUALIZACION = ['Subir', 'Aprobar', 'Aplicar', 'Revisar en Control'];


const ESTADO_LABEL = {
  SIN_CAMBIOS: 'Sin cambios',
  DIFERENCIAS: 'Con diferencias',
  CONFLICTO_HASH: 'Conflicto (hash)',
  CONFLICTO_UPDATED_AT: 'Conflicto (updated_at)',
  ID_PROTEGIDO_MODIFICADO: 'Identificador protegido modificado',
  DISPOSITIVO_INCONSISTENTE: 'Dispositivo inconsistente',
  SUSCRIPCION_NO_ENCONTRADA: 'Suscripción no encontrada',
};


export default function ActualizacionSuscripciones() {
  // Iter52.91 · B2 · Las descargas avisan de que están trabajando.
  const {descargando, conEstadoDescarga} = useDescargas();
  const [archivo, setArchivo] = useState(null);
  const [sesion, setSesion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientesAbiertos, setClientesAbiertos] = useState({});
  const [invalidasAbiertos, setInvalidasAbiertos] = useState({});
  const [aplicando, setAplicando] = useState(false);
  const [resumenPrevio, setResumenPrevio] = useState(null);
  const [resultado, setResultado] = useState(null);
  // Iter52.53 · D4 · "Última aplicación" persistente en servidor.
  // Al montar y tras aplicar, consultamos `GET /saas/op-status/aplicar_excel`.
  const [ultimaAplicacion, setUltimaAplicacion] = useState(null);
  // Iter52.61 · 2 · El análisis y la aplicación van en segundo plano: aquí
  // se guarda el progreso que publica el backend.
  const [prog, setProg] = useState(null);   // {estado, progreso:{fase,hechas,total}}
  const sesionIdRef = React.useRef(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await saasApi.get('/saas/op-status/aplicar_excel');
        setUltimaAplicacion(r.data?.at ? r.data : null);
      } catch (_) { /* silencioso */ }
    })();
  }, []);
  const _refreshUltima = async () => {
    try {
      const r = await saasApi.get('/saas/op-status/aplicar_excel');
      setUltimaAplicacion(r.data?.at ? r.data : null);
    } catch (_) { /* silencioso */ }
  };

  // Iter52.62 · B6 · Informe PDF de la carga: se genera al aplicar y se
  // descarga automáticamente; el enlace queda visible por si el navegador
  // bloquea la descarga.
  const [informeId, setInformeId] = useState(null);
  const [informesPrevios, setInformesPrevios] = useState([]);
  const cargarInformesPrevios = React.useCallback(async () => {
    try {
      const r = await saasApi.get('/saas/informes-carga?tipo=ACTUALIZACION_SUSCRIPCIONES&limit=25');
      setInformesPrevios(r.data?.items || []);
    } catch (_) { /* solo super_admin */ }
  }, []);
  useEffect(() => { cargarInformesPrevios(); }, [cargarInformesPrevios]);
  const esperarInforme = React.useCallback(async (sid, idInicial) => {
    let id = idInicial;
    for (let i = 0; i < 8 && !id; i += 1) {
      await new Promise(res => setTimeout(res, 2000));
      try {
        const r = await saasApi.get(`/saas/admin/actualizacion-suscripciones/sesion/${sid}/progreso`);
        id = r.data?.informe_id;
      } catch (_) { /* reintento */ }
    }
    if (id) {
      setInformeId(id);
      descargarInformeCarga(id, { silencioso: true });
      cargarInformesPrevios();
    }
  }, [cargarInformesPrevios]);

  const analizar = async () => {
    if (!archivo) return toast.error('Selecciona el archivo generado desde Control');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      const r = await saasApi.post('/saas/admin/actualizacion-suscripciones/analizar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Respuesta inmediata con el id de sesión; el análisis sigue en
      // segundo plano y lo seguimos por `/progreso`.
      setSesion(null); setResultado(null);
      sesionIdRef.current = r.data.session_id;
      setProg({ estado: 'ANALIZANDO', progreso: { fase: 'ANALIZANDO', hechas: 0, total: 0 } });
      toast.info(r.data.mensaje || 'Analizando…');
    } catch (e) {
      toast.error(errText(e));
      setLoading(false);
    }
  };

  // Iter52.61 · 2 · Sondeo del progreso mientras analiza o aplica. Si el
  // usuario recarga, al volver con el id de sesión ve el estado real.
  const cargarSesion = React.useCallback(async (sid) => {
    const r = await saasApi.get(`/saas/admin/actualizacion-suscripciones/sesion/${sid}`);
    setSesion({ session_id: sid, cabecera: r.data.cabecera, decisiones: r.data.decisiones });
  }, []);
  useEffect(() => {
    const enCurso = prog && ['ANALIZANDO', 'APLICANDO'].includes(prog.estado);
    if (!enCurso || !sesionIdRef.current) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await saasApi.get(`/saas/admin/actualizacion-suscripciones/sesion/${sesionIdRef.current}/progreso`);
        setProg(r.data);
        if (r.data.estado === 'PENDIENTE') {
          await cargarSesion(sesionIdRef.current);
          setLoading(false);
          toast.success('Análisis completado');
        } else if (r.data.estado === 'APLICADA') {
          setResultado(r.data.resultado || {});
          setAplicando(false); setResumenPrevio(null);
          await _refreshUltima();
          toast.success(`Aplicación finalizada · ${(r.data.resultado||{}).aplicadas||0} filas aplicadas`);
          // Iter52.62 · B6 · el informe PDF se genera justo después de
          // aplicar: lo esperamos unos segundos y se descarga solo.
          esperarInforme(sesionIdRef.current, r.data.informe_id);
        } else if (r.data.estado === 'ERROR') {
          setLoading(false); setAplicando(false);
          toast.error(r.data.error || 'El proceso terminó con error');
        }
      } catch (e) { /* se reintenta en el siguiente tick */ }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prog?.estado, cargarSesion]);

  const decidir = async (decId, campo, estado) => {
    try {
      await saasApi.post(`/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}/decidir`,
        { decision_id: decId, campo, estado });
      const r = await saasApi.get(`/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}`);
      setSesion({ session_id: sesion.session_id, cabecera: r.data.cabecera, decisiones: r.data.decisiones });
      if (estado === 'APROBADA') toast.success(`Diferencia aprobada · Pendiente de aplicar`);
    } catch (e) { toast.error(errText(e)); }
  };

  const decidirMasivo = async (campo, estado) => {
    try {
      const r0 = await saasApi.post(`/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}/decidir-masivo`,
        { campo, estado });
      const r = await saasApi.get(`/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}`);
      setSesion({ session_id: sesion.session_id, cabecera: r.data.cabecera, decisiones: r.data.decisiones });
      toast.success(estado==='APROBADA'
        ? `${r0.data.cambiadas} diferencias de ${campo} aprobadas · Pendientes de aplicar`
        : `${r0.data.cambiadas} diferencias de ${campo} rechazadas`);
    } catch (e) { toast.error(errText(e)); }
  };

  // Iter52.61 · 3 · «Aprobar todas» = una sola operación de BBDD.
  const aprobarTodas = async () => {
    try {
      const r0 = await saasApi.post(
        `/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}/aprobar-todas`);
      await cargarSesion(sesion.session_id);
      toast.success(`${r0.data.cambiadas} diferencias aprobadas · Pendientes de aplicar`
        + (r0.data.excluidas_con_error ? ` · ${r0.data.excluidas_con_error} excluidas por validación` : ''));
    } catch (e) { toast.error(errText(e)); }
  };

  const abrirConfirmacion = async () => {
    try {
      const r = await saasApi.get(`/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}/resumen-previo`);
      setResumenPrevio(r.data);
    } catch (e) { toast.error(errText(e)); }
  };

  const aplicar = async () => {
    setAplicando(true);
    try {
      const r = await saasApi.post(`/saas/admin/actualizacion-suscripciones/sesion/${sesion.session_id}/aplicar`);
      sesionIdRef.current = sesion.session_id;
      setResumenPrevio(null);
      if (r.data.ya_en_curso) {
        // Segunda confirmación mientras aplica: aviso claro, no error.
        toast.info(r.data.mensaje);
      } else if (r.data.estado === 'APLICADA') {
        setResultado(r.data.resultado || {});
        setAplicando(false);
        toast.info(r.data.mensaje);
        return;
      } else {
        toast.info(r.data.mensaje || 'Aplicando en segundo plano…');
      }
      setProg({ estado: 'APLICANDO', progreso: r.data.progreso || { fase: 'APLICANDO', hechas: 0, total: 0 } });
    } catch (e) {
      toast.error(errText(e));
      setAplicando(false);
    }
  };

  const contadores = sesion?.cabecera?.contadores || {};
  // Iter52.61 · 3 · contadores globales de la barra fija.
  const { contApr, contPend, contRech } = useMemo(() => {
    let a = 0, p = 0, r = 0;
    (sesion?.decisiones || []).forEach(dec => (dec.diferencias || []).forEach(d => {
      if (d.decision === 'APROBADA') a += 1;
      else if (d.decision === 'PENDIENTE') p += 1;
      else r += 1;
    }));
    return { contApr: a, contPend: p, contRech: r };
  }, [sesion]);
  const ocupado = aplicando || (prog && ['ANALIZANDO', 'APLICANDO'].includes(prog.estado));
  const decisionesConDiffs = useMemo(
    () => (sesion?.decisiones || []).filter(d => (d.diferencias || []).length > 0),
    [sesion]);

  // Agrupación por campo para masivas (excluye sensibles y NO_APROBABLE_FASE2)
  const agrupadasPorCampo = useMemo(() => {
    const g = {};
    decisionesConDiffs.forEach(dec => {
      (dec.diferencias || []).forEach(x => {
        if (!x.editable) return;
        if (x.decision !== 'PENDIENTE') return;
        if (x.sensible) return;  // reactivaciones sensibles → individual
        g[x.campo] = (g[x.campo] || 0) + 1;
      });
    });
    return g;
  }, [decisionesConDiffs]);

  // Filas con al menos una diferencia sensible pendiente (para señalización)
  const filasConSensible = useMemo(() =>
    decisionesConDiffs.filter(d => (d.diferencias || []).some(x => x.sensible && x.decision === 'PENDIENTE')),
  [decisionesConDiffs]);

  // Agrupación por cliente (Iter52.7 addendum · panel colapsado)
  const diffsPorCliente = useMemo(() => {
    const g = {};
    decisionesConDiffs.forEach(dec => {
      const k = dec.cliente_snapshot || '—';
      (g[k] = g[k] || []).push(dec);
    });
    return g;
  }, [decisionesConDiffs]);

  const invalidas = useMemo(() =>
    (sesion?.decisiones || []).filter(d => d.resultado !== 'SIN_CAMBIOS' && d.resultado !== 'DIFERENCIAS'),
  [sesion]);

  const invalidasPorCliente = useMemo(() => {
    const g = {};
    invalidas.forEach(d => {
      const k = d.cliente_snapshot || '—';
      (g[k] = g[k] || []).push(d);
    });
    return g;
  }, [invalidas]);

  // Iter52.63 · P1 · Paso actual y bloqueo del botón «Aplicar» tras aplicar.
  const yaAplicada = !!resultado || prog?.estado === 'APLICADA';
  const pasoActual = yaAplicada ? 3 : (contApr > 0 ? 2 : 1);

  if (!sesion) {
    return <div className="space-y-4" data-testid="actu-panel-upload">
      {/* Iter52.63 · P1 · Pasos con estado. */}
      <PasosCarga pasos={PASOS_ACTUALIZACION} actual={0} testid="pasos-actualizacion"/>
      {ultimaAplicacion && (
        <div data-testid="banner-ultima-aplicacion" className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-sm text-emerald-900">
          Última aplicación: <b>{ultimaAplicacion.aplicadas} aplicadas</b>, <b>{ultimaAplicacion.pendientes} pendientes</b>, a las <b>{new Date(ultimaAplicacion.at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</b>{ultimaAplicacion.resumen ? <span className="text-emerald-700"> · {ultimaAplicacion.resumen}</span> : null}
        </div>
      )}
      <div className="bg-white border rounded-2xl p-6">
        <p className="text-sm font-bold mb-2">Actualización de suscripciones · v1</p>
        <p className="text-sm text-slate-600 mb-4">
          Sube el archivo XLSX generado desde <b>Control → Descargar actualización de
          suscripciones</b>. El nuevo canal compara literalmente cada campo con la BBDD.
          Cero derivaciones, cero acceso a MAESTRO histórico.
        </p>
        <label className="btn-secondary inline-flex" data-testid="btn-seleccionar-archivo">
          <UploadCloud className="w-4 h-4"/>Seleccionar archivo
          <input type="file" accept=".xlsx" hidden onChange={e => setArchivo(e.target.files?.[0] || null)}/>
        </label>
        {archivo && <span className="ml-3 text-sm text-slate-600">{archivo.name}</span>}
        <div className="mt-4">
          <button data-testid="btn-analizar-actualizacion" className="btn-primary" disabled={!archivo || loading} onClick={analizar}>
            {loading ? 'Analizando…' : 'Analizar diferencias'}
          </button>
          {/* Iter52.61 · 2 · el análisis va en segundo plano: progreso aquí. */}
          {prog && prog.estado === 'ANALIZANDO' && <BarraProgreso
            testid="progreso-analisis" etiqueta="Analizando" prog={prog.progreso}/>}
          {prog && prog.estado === 'ERROR' && <p className="text-sm text-rose-700 mt-3" data-testid="progreso-error">{prog.error}</p>}
        </div>
      </div>
    </div>;
  }

  return <div className="space-y-5" data-testid="actu-panel-revision">
    {/* Iter52.63 · P1 · Pasos: Subir → Aprobar → Aplicar → Revisar en Control. */}
    <PasosCarga pasos={PASOS_ACTUALIZACION} actual={pasoActual} testid="pasos-actualizacion"/>
    {/* Iter52.63 · P1 · Banner FIJO del resultado (ya no solo un toast). */}
    {resultado && <div className="bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-3 text-sm text-emerald-900 flex items-center justify-between gap-3" data-testid="banner-resultado-aplicacion">
      <span>
        Aplicación finalizada · <b>{resultado.aplicadas || 0} filas aplicadas</b>
        {resultado.fallidas ? <> · <b className="text-rose-700">{resultado.fallidas} fallidas</b></> : null}
        {resultado.costes_rellenados ? <> · {resultado.costes_rellenados} costes Geotab rellenados</> : null}
      </span>
      <a className="btn-secondary" href="/admin/saas/control?seccion=activaciones" data-testid="btn-siguiente-control">Siguiente: Control → Aprobaciones</a>
    </div>}
    {ultimaAplicacion && !resultado && (
      <div data-testid="banner-ultima-aplicacion" className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-sm text-emerald-900">
        Última aplicación: <b>{ultimaAplicacion.aplicadas} aplicadas</b>, <b>{ultimaAplicacion.pendientes} pendientes</b>, a las <b>{new Date(ultimaAplicacion.at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</b>{ultimaAplicacion.resumen ? <span className="text-emerald-700"> · {ultimaAplicacion.resumen}</span> : null}
      </div>
    )}
    {/* ══ Iter52.61 · 3 · Barra fija: aprobar y aplicar sin bajar al final ══ */}
    <div className="sticky top-0 z-30 -mx-1 px-4 py-3 bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-sm flex items-center gap-3 flex-wrap"
         data-testid="barra-acciones-fija">
      <span className="text-sm font-bold text-slate-700 mr-auto" data-testid="barra-contadores">
        <b className="text-emerald-700 tabular-nums">{contApr}</b> aprobadas ·
        <b className="text-amber-700 tabular-nums ml-1">{contPend}</b> pendientes ·
        <b className="text-rose-700 tabular-nums ml-1">{contRech}</b> rechazadas ·
        {/* Iter52.63 · P1 · contador de aplicadas junto a las aprobadas. */}
        <b className="text-slate-900 tabular-nums ml-1" data-testid="barra-aplicadas">{resultado?.aplicadas || 0}</b> aplicadas
      </span>
      <button data-testid="btn-aprobar-todas" className="btn-secondary"
              disabled={ocupado || !contPend || yaAplicada} onClick={aprobarTodas}>
        Aprobar todas{contPend ? ` · ${contPend}` : ''}
      </button>
      {/* Iter52.63 · P1 · tras aplicar queda desactivado y pasa a «Aplicada»
          (evita el doble aplicar; pendiente desde 52.61). */}
      <button data-testid="btn-aplicar-fijo" className="btn-primary"
              disabled={ocupado || !contApr || yaAplicada} onClick={abrirConfirmacion}>
        {yaAplicada ? 'Aplicada' : ocupado ? 'Aplicando…' : `Aplicar · ${contApr}`}
      </button>
    </div>
    {prog && ['APLICANDO'].includes(prog.estado) && <BarraProgreso
      testid="progreso-aplicacion" etiqueta="Aplicando" prog={prog.progreso}/>}

    <div className="bg-white border rounded-2xl p-5">
      <p className="text-xs uppercase font-bold tracking-wide text-slate-500 mb-2">Resumen del análisis</p>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Total" value={contadores.total||0} testid="stat-total"/>
        <Stat label="Sin cambios" value={contadores.sin_cambios||0} tone="green" testid="stat-sin-cambios"/>
        <Stat label="Con diferencias" value={contadores.diferencias||0} tone="amber" testid="stat-diferencias"/>
        <Stat label="Diferencias totales" value={contadores.diffs_totales||0} testid="stat-diffs-totales"/>
        <Stat label="Conflictos" value={contadores.conflictos||0} tone="red" testid="stat-conflictos"/>
        <Stat label="Inválidas" value={contadores.invalidas||0} tone="red" testid="stat-invalidas"/>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button className="btn-secondary" onClick={() => { setSesion(null); setArchivo(null); setResultado(null); }}>Volver a subir</button>
        <button data-testid="btn-aplicar-actualizacion" className="btn-primary" disabled={ocupado || yaAplicada} onClick={abrirConfirmacion}>
          {yaAplicada ? 'Aplicada' : aplicando ? 'Aplicando…' : 'Revisar y aplicar decisiones aprobadas'}
        </button>
      </div>
    </div>

    {resumenPrevio && <ModalConfirmacion resumen={resumenPrevio} onCancel={()=>setResumenPrevio(null)} onConfirm={aplicar} loading={aplicando}/>}

    {/* Iter52.62 · B6 · Informe PDF de la carga aplicada + histórico. */}
    {informeId && <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 text-sm text-sky-900 flex items-center justify-between gap-3" data-testid="aviso-informe-pdf">
      <span>Informe de esta carga guardado en el histórico. La descarga empieza sola; si el navegador la bloquea, usa el enlace.</span>
      <button className="btn-secondary disabled:opacity-60 disabled:cursor-not-allowed" data-testid="btn-descargar-informe-actualizacion" disabled={descargando('informe-pdf')}
              onClick={()=>conEstadoDescarga('informe-pdf',()=>descargarInformeCarga(informeId))}>{descargando('informe-pdf')?'Generando…':'Descargar informe (PDF)'}</button>
    </div>}
    {informesPrevios.length > 0 && <details className="bg-white border rounded-2xl p-4 text-sm" data-testid="historico-informes-actualizacion">
      <summary className="cursor-pointer font-bold">Informes de cargas anteriores ({informesPrevios.length})</summary>
      <ul className="mt-2 divide-y">
        {informesPrevios.map(inf => <li key={inf.id} className="py-2 flex items-center justify-between gap-3">
          <div>
            <p className="font-mono text-xs">{inf.filename || '(sin nombre)'}</p>
            <p className="text-xs text-slate-500">{inf.fecha ? new Date(inf.fecha).toLocaleString('es-ES') : ''} · {inf.usuario} · {inf.afecta_facturacion} cambios de facturación</p>
          </div>
          <button className="link text-xs disabled:opacity-60 disabled:cursor-not-allowed" data-testid={`btn-pdf-informe-${inf.id}`} disabled={descargando(`pdf-${inf.id}`)}
                  onClick={()=>conEstadoDescarga(`pdf-${inf.id}`,()=>descargarInformeCarga(inf.id))}>{descargando(`pdf-${inf.id}`)?'Generando…':'PDF'}</button>
        </li>)}
      </ul>
    </details>}

    {resultado && <ResultadoAgrupado resultado={resultado}/>}

    {Object.keys(agrupadasPorCampo).length > 0 && <div className="bg-white border rounded-2xl p-4" data-testid="panel-masivas">
      <p className="text-xs uppercase font-bold tracking-wide text-slate-500 mb-2">Aprobación masiva por columna</p>
      <p className="text-xs text-slate-600 mb-3">Excluye reactivaciones sensibles desde BAJA, que requieren aprobación individual expresa.</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(agrupadasPorCampo).map(([campo, n]) => <div key={campo} className="inline-flex items-center gap-1 border rounded-lg px-2 py-1 bg-slate-50">
          <span className="text-xs font-bold">{campo}</span>
          <span className="text-xs text-slate-500">· {n} pend.</span>
          <button data-testid={`btn-masivo-aprobar-${campo}`} className="link text-xs" onClick={() => decidirMasivo(campo, 'APROBADA')}>Aprobar {n}</button>
          <button data-testid={`btn-masivo-rechazar-${campo}`} className="link text-xs text-red-600" onClick={() => decidirMasivo(campo, 'RECHAZADA_USUARIO')}>Rechazar {n}</button>
        </div>)}
      </div>
    </div>}

    {filasConSensible.length > 0 && <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-4" data-testid="panel-sensibles">
      <p className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4"/> Reactivaciones sensibles ({filasConSensible.length})</p>
      <p className="text-xs text-amber-900">Estas filas requieren aprobación individual expresa (transición desde BAJA). Están excluidas de la aprobación masiva. Se aplicarán con la operación canónica de reactivación (compartida con el endpoint HTTP `POST /saas/suscripciones/&#123;id&#125;/reactivar`).</p>
    </div>}

    <div className="bg-white border rounded-2xl overflow-hidden">
      <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
        <span className="text-sm font-bold">Diferencias por fila</span>
        <span className="text-xs text-slate-500">{decisionesConDiffs.length} suscripciones · {Object.keys(diffsPorCliente).length} clientes</span>
      </div>
      <div className="divide-y max-h-[65vh] overflow-auto">
        {decisionesConDiffs.length === 0 && <p className="p-6 text-center text-sm text-slate-400">Sin diferencias que aprobar.</p>}
        {Object.entries(diffsPorCliente).sort(([, a], [, b]) => b.length - a.length).map(([cliente, decs]) => {
          const abierto = !!clientesAbiertos[cliente];
          const totalDiffs = decs.reduce((n, d) => n + (d.diferencias || []).length, 0);
          return <div key={cliente} data-testid={`cliente-${cliente}`}>
            <button className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center justify-between"
              onClick={() => setClientesAbiertos(o => ({ ...o, [cliente]: !abierto }))}>
              <span className="text-sm font-medium">
                {abierto ? <ChevronDown className="w-3 h-3 inline"/> : <ChevronRight className="w-3 h-3 inline"/>}
                {' '}{cliente}
              </span>
              <span className="text-xs text-slate-500">{decs.length} dispositivo{decs.length !== 1 ? 's' : ''} · {totalDiffs} diferencia{totalDiffs !== 1 ? 's' : ''}</span>
            </button>
            {abierto && <div className="bg-slate-50 border-t border-b py-2 px-3 space-y-2">
              {decs.map(dec => <div key={dec.id} data-testid={`fila-${dec.suscripcion_id}`} className="bg-white border rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">
                    <span className="font-mono">{dec.matricula_snapshot || dec.suscripcion_id?.slice(0, 8)}</span>
                    {dec.serial_geotab_snapshot && <span className="text-slate-500 ml-2">· {dec.serial_geotab_snapshot}</span>}
                  </span>
                  <span className="text-xs text-slate-500">{(dec.diferencias || []).length} diferencia{(dec.diferencias || []).length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-1">
                  {(dec.diferencias || []).map(diff => <DiffLinea key={diff.campo} dec={dec} diff={diff} onDecidir={decidir}/>)}
                </div>
              </div>)}
            </div>}
          </div>;
        })}
      </div>
    </div>

    {invalidas.length > 0 && <div className="bg-red-50 border border-red-200 rounded-2xl overflow-hidden" data-testid="panel-invalidas">
      <div className="p-3 border-b border-red-200 bg-red-100/60 flex items-center justify-between">
        <span className="text-sm font-bold text-red-900">Filas no aplicables ({invalidas.length})</span>
        <span className="text-xs text-red-700">{Object.keys(invalidasPorCliente).length} clientes</span>
      </div>
      <div className="divide-y divide-red-200 max-h-[45vh] overflow-auto">
        {Object.entries(invalidasPorCliente).sort(([, a], [, b]) => b.length - a.length).map(([cliente, decs]) => {
          const abierto = !!invalidasAbiertos[cliente];
          return <div key={cliente}>
            <button className="w-full text-left px-4 py-2 hover:bg-red-100/40 flex items-center justify-between"
              onClick={() => setInvalidasAbiertos(o => ({ ...o, [cliente]: !abierto }))}>
              <span className="text-sm font-medium text-red-900">
                {abierto ? <ChevronDown className="w-3 h-3 inline"/> : <ChevronRight className="w-3 h-3 inline"/>}
                {' '}{cliente}
              </span>
              <span className="text-xs text-red-700">{decs.length} no aplicable{decs.length !== 1 ? 's' : ''}</span>
            </button>
            {abierto && <div className="bg-white/60 border-t border-red-200 py-2 px-3 space-y-1">
              {decs.map(d => <div key={d.id} className="text-xs text-red-800 flex items-center justify-between gap-3">
                <span>
                  <span className="font-mono">{d.matricula_snapshot || d.suscripcion_id?.slice(0, 8)}</span>
                  {d.serial_geotab_snapshot && <span className="text-red-700/70 ml-2">· {d.serial_geotab_snapshot}</span>}
                </span>
                <span className="font-semibold">{ESTADO_LABEL[d.resultado] || d.resultado}</span>
              </div>)}
            </div>}
          </div>;
        })}
      </div>
    </div>}
  </div>;
}


function Stat({ label, value, tone = 'slate', testid }) {
  const tones = {
    slate: 'bg-slate-50 text-slate-800',
    green: 'bg-emerald-50 text-emerald-800',
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-800',
  };
  return <div data-testid={testid} className={`rounded-xl p-3 text-center ${tones[tone]}`}>
    <p className="text-3xl font-black">{value}</p>
    <p className="text-xs">{label}</p>
  </div>;
}


function DiffLinea({ dec, diff, onDecidir }) {
  const err = diff.error_validacion;
  const editable = diff.editable && !err && diff.decision !== 'NO_APROBABLE_FASE2' && diff.decision !== 'RECHAZADA_TRANSICION_NO_PERMITIDA';
  const isBorrado = diff.tipo === 'BORRADO';
  const esInformativa = diff.aplicacion === 'producto_saas_fase2' || diff.decision === 'NO_APROBABLE_FASE2';
  const esSensible = diff.sensible;
  const esRechazoTransicion = diff.decision === 'RECHAZADA_TRANSICION_NO_PERMITIDA';
  const aprobada = diff.decision === 'APROBADA';
  const rechazada = diff.decision === 'RECHAZADA_USUARIO';
  // Estilos inequívocos: fondo sólido, borde grueso, badge grande.
  const wrap = aprobada
    ? 'bg-emerald-100 border-2 border-emerald-500 shadow-sm'
    : rechazada
    ? 'bg-slate-200 border-2 border-slate-500'
    : esInformativa ? 'bg-blue-50 border border-blue-300'
    : esRechazoTransicion ? 'bg-red-50 border border-red-300'
    : esSensible ? 'bg-white border-2 border-amber-400'
    : 'bg-white border';
  const badgeCls = aprobada ? 'bg-emerald-600 text-white'
    : rechazada ? 'bg-slate-600 text-white'
    : diff.decision === 'RECHAZADA_NO_EDITABLE' ? 'bg-red-500 text-white'
    : diff.decision === 'NO_APROBABLE_FASE2' ? 'bg-blue-600 text-white'
    : diff.decision === 'RECHAZADA_TRANSICION_NO_PERMITIDA' ? 'bg-red-600 text-white'
    : 'bg-amber-500 text-white';
  const badgeTxt = aprobada ? 'APROBADA' : rechazada ? 'RECHAZADA' : diff.decision;
  return <div className={`rounded-lg p-2 flex items-center justify-between gap-2 text-xs ${wrap}`} data-testid={`diff-${dec.suscripcion_id}-${diff.campo}`}>
    <div className="flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <b className="text-sm">{diff.campo}</b>
        <span className={`inline-block px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${badgeCls}`}>{badgeTxt}</span>
        {isBorrado && <span className="text-orange-800 font-semibold text-[10px]">PROPUESTA DE BORRADO</span>}
        {esSensible && <span className="inline-flex items-center gap-1 px-1.5 rounded bg-amber-100 border border-amber-500 text-amber-900 font-semibold text-[10px]" data-testid={`badge-sensible-${dec.suscripcion_id}`}>SENSIBLE · Reactivación individual</span>}
        {esInformativa && <span className="inline-flex items-center gap-1 px-1.5 rounded bg-blue-100 border border-blue-400 text-blue-900 font-semibold text-[10px]" data-testid={`badge-fase2-${dec.suscripcion_id}`}>FASE 2 · No aprobable ahora</span>}
        {esRechazoTransicion && <span className="inline-flex items-center gap-1 px-1.5 rounded bg-red-100 border border-red-400 text-red-900 font-semibold text-[10px]">Transición no permitida</span>}
      </div>
      <div className="mt-1">
        <span className="text-slate-700"><b>Anterior:</b> <code className="bg-white/70 px-1 rounded">{diff.valor_actual || '∅'}</code></span>
        <span className="ml-3 text-slate-700"><b>Propuesto:</b> <code className="bg-white/70 px-1 rounded">{diff.valor_recibido || '∅'}</code></span>
      </div>
      {err && <div className="text-red-700 mt-1"><AlertTriangle className="inline w-3 h-3"/> {err}</div>}
      {diff.motivo && !err && <div className="text-slate-700 mt-1 italic">{diff.motivo}</div>}
      {/* Iter52.60 · Rama 29 · valores derivados de las columnas abiertas
          (coste del plan, fecha límite e inicio de cobro recalculados). */}
      {diff.valor_derivado && <div className="mt-1 text-[11px] text-slate-600 bg-white/70 rounded px-2 py-1" data-testid={`derivado-${diff.campo}`}>
        {Object.entries(diff.valor_derivado).map(([k, v]) => (
          <span key={k} className="mr-3">{k.replace(/_/g, ' ')}: <b>{String(v ?? '∅')}</b></span>
        ))}
      </div>}
      {!diff.editable && <div className="text-slate-600 mt-1">Columna no editable en este canal · el cambio no se aplica</div>}
    </div>
    <div className="flex gap-1 shrink-0">
      {editable && diff.decision === 'PENDIENTE' && <>
        <button data-testid={`btn-aprobar-${dec.suscripcion_id}-${diff.campo}`} className={`text-xs px-3 py-1.5 rounded-lg font-bold ${esSensible ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
          onClick={() => {
            if (esSensible) {
              const ok = window.confirm(`Confirmar REACTIVACIÓN desde BAJA (${diff.valor_actual} → ${diff.valor_recibido}).\n\nEsta acción requiere confirmación individual expresa.\n\n¿Continuar?`);
              if (!ok) return;
            }
            onDecidir(dec.id, diff.campo, 'APROBADA');
          }}>{esSensible ? 'Aprobar reactivación' : 'Aprobar'}</button>
        <button data-testid={`btn-rechazar-${dec.suscripcion_id}-${diff.campo}`} className="text-xs px-3 py-1.5 rounded-lg font-bold bg-white border border-red-400 text-red-700 hover:bg-red-50"
          onClick={() => onDecidir(dec.id, diff.campo, 'RECHAZADA_USUARIO')}>Rechazar</button>
      </>}
      {editable && diff.decision !== 'PENDIENTE' && <button data-testid={`btn-deshacer-${dec.suscripcion_id}-${diff.campo}`} className="text-xs px-3 py-1.5 rounded-lg font-bold bg-white border border-slate-400 text-slate-800 hover:bg-slate-100" onClick={() => onDecidir(dec.id, diff.campo, 'PENDIENTE')}>Deshacer</button>}
    </div>
  </div>;
}

function ModalConfirmacion({ resumen, onCancel, onConfirm, loading }){
  const [ack, setAck] = React.useState(false);
  const hayConsecuencias = (resumen.consecuencias||[]).length > 0;
  return <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="modal-confirmar-aplicar">
    <div className="bg-white rounded-2xl max-w-2xl w-full p-5 space-y-3 max-h-[85vh] overflow-auto">
      <p className="text-lg font-black">Confirmar aplicación</p>
      <p className="text-sm text-slate-600">
        Vas a aplicar <b>{resumen.campos_aprobados}</b> diferencias aprobadas repartidas en <b>{resumen.filas}</b> filas.
        La operación es transaccional por fila; se releerá BBDD dentro y después del commit para confirmar cada cambio (y cada consecuencia automática).
      </p>
      <div className="border rounded-lg p-3 bg-slate-50">
        <p className="text-xs uppercase font-bold text-slate-500 mb-1">Cambios solicitados por el usuario (por campo)</p>
        {Object.entries(resumen.por_campo||{}).map(([c,n])=><div key={c} className="text-xs"><b>{c}</b>: {n}</div>)}
      </div>
      {hayConsecuencias && <div className="border-2 border-purple-400 rounded-lg p-3 bg-purple-50" data-testid="panel-consecuencias">
        <p className="text-xs uppercase font-bold text-purple-900 mb-2">Consecuencias automáticas obligatorias ({resumen.consecuencias.length})</p>
        <p className="text-xs text-purple-800 mb-2">Derivadas de reglas existentes. Se ejecutarán junto con los cambios aprobados y se verifican dos veces (dentro y fuera de la transacción).</p>
        <div className="space-y-1 max-h-40 overflow-auto">
          {resumen.consecuencias.map((c,i)=><div key={i} className="text-xs text-purple-900" data-testid={`consecuencia-${i}`}>
            · <b>[{c.tipo}]</b> {c.suscripcion_id?.slice(0,8)}… — {c.descripcion}
          </div>)}
        </div>
      </div>}
      {(resumen.sensibles||[]).length > 0 && <div className="border rounded-lg p-3 bg-amber-50 border-amber-300" data-testid="warn-sensibles">
        <p className="text-xs font-bold text-amber-900 mb-1">Cambios sensibles (aprobación individual)</p>
        {resumen.sensibles.map((s,i)=><div key={i} className="text-xs text-amber-900">· {s.suscripcion_id?.slice(0,8)}… {s.campo==='ESTADO_DISPOSITIVO'?`${s.campo} → ${s.valor_recibido}`:'reactivación desde BAJA'} (excluida de aprobación masiva)</div>)}
      </div>}
      {/* Iter52.99 · Total de bajas que arrastran los MUERTO aprobados. */}
      {!!resumen.bajas_arrastradas && <div className="border-2 rounded-lg p-3 bg-red-50 border-red-300" data-testid="warn-bajas-arrastradas">
        <p className="text-xs font-bold text-red-900">Bajas arrastradas: {resumen.bajas_arrastradas} suscripciones</p>
        <p className="text-xs text-red-800">Los dispositivos que pasan a MUERTO dan de baja sus suscripciones vivas en la misma operación.</p>
      </div>}
      {(resumen.informativas_fase2||[]).length > 0 && <div className="border rounded-lg p-3 bg-blue-50 border-blue-300">
        <p className="text-xs font-bold text-blue-900 mb-1">Cambios informativos</p>
        <p className="text-xs text-blue-800">{resumen.informativas_fase2.length} cambio(s) se detectarán como INFORMATIVA y no se aplicarán por este canal.</p>
      </div>}
      <label className="flex items-start gap-2 text-xs text-slate-700 border-t pt-2">
        <input type="checkbox" checked={ack} onChange={e=>setAck(e.target.checked)} data-testid="chk-acepto-consecuencias" className="mt-0.5"/>
        <span>Confirmo el conjunto completo: cambios solicitados <b>+</b> consecuencias automáticas derivadas de las reglas existentes.</span>
      </label>
      <div className="flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onCancel} disabled={loading} data-testid="btn-cancelar-aplicar">Cancelar</button>
        <button className="btn-primary" onClick={onConfirm} disabled={loading || !ack} data-testid="btn-confirmar-aplicar">{loading?'Aplicando…':'Confirmar y aplicar'}</button>
      </div>
    </div>
  </div>;
}



const MOTIVOS_LABEL = {
  SIN_PERIODO_ACTIVO_O_CREACION_IMPOSIBLE: 'Sin período activo o creación de período imposible',
  TRANSICION_NO_PERMITIDA: 'Transición no permitida',
  CONFLICTO_HASH: 'Conflicto (hash / updated_at)',
  ERROR_VALIDACION: 'Error de validación',
  FALLO_POSTVERIFICACION: 'Fallo de postverificación',
  OTROS_MOTIVOS: 'Otros motivos',
};

function ResultadoAgrupado({ resultado }) {
  const [openGrupo, setOpenGrupo] = React.useState({});
  const [openCliente, setOpenCliente] = React.useState({});
  const detalle = resultado.detalle || [];
  // Agrupación por causa (motivo_grupo). Sólo filas fallidas/conflicto/postverif.
  const grupos = React.useMemo(() => {
    const g = {};
    for (const d of detalle) {
      if (d.resultado_fila === 'APLICADA') continue;
      const key = d.motivo_grupo || (d.resultado_fila === 'CONFLICTO' ? 'CONFLICTO_HASH' : 'OTROS_MOTIVOS');
      (g[key] = g[key] || []).push(d);
    }
    return g;
  }, [detalle]);
  // Agrupación por cliente (todas las filas)
  const clientes = React.useMemo(() => {
    const m = {};
    for (const d of detalle) {
      const key = d.cliente_nombre || '— sin cliente —';
      (m[key] = m[key] || []).push(d);
    }
    return m;
  }, [detalle]);
  return <div className="bg-white border rounded-2xl p-4 space-y-4" data-testid="actu-resultado">
    <div>
      <p className="font-bold text-slate-900 mb-2">Resultado de la aplicación</p>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        <Stat testid="res-aplicadas" label="Filas aplicadas" value={resultado.aplicadas || 0} tone="green"/>
        <Stat testid="res-fallidas" label="Filas fallidas" value={resultado.fallidas || 0} tone={resultado.fallidas ? 'red' : 'slate'}/>
        <Stat testid="res-conflictos" label="Conflictos" value={resultado.conflictos || 0} tone={resultado.conflictos ? 'amber' : 'slate'}/>
        <Stat testid="res-campos-ok" label="Campos aplicados" value={resultado.campos_aplicados || 0} tone="green"/>
        <Stat testid="res-campos-ko" label="Campos fallidos" value={resultado.campos_fallidos || 0} tone={resultado.campos_fallidos ? 'red' : 'slate'}/>
      </div>
    </div>

    {Object.keys(grupos).length > 0 && <div>
      <p className="text-xs uppercase font-bold text-slate-500 mb-2">Filas no aplicadas · agrupadas por causa</p>
      <div className="space-y-2">
        {Object.entries(grupos).map(([motivo, filas]) => {
          const numCampos = filas.reduce((n, f) => n + (f.campos || []).length, 0);
          const clientesUnicos = new Set(filas.map(f => f.cliente_nombre || '—')).size;
          const open = !!openGrupo[motivo];
          return <div key={motivo} data-testid={`grupo-${motivo}`} className="border rounded-lg overflow-hidden">
            <button className="w-full text-left px-3 py-2 bg-slate-50 hover:bg-slate-100 flex items-center justify-between"
              onClick={() => setOpenGrupo(o => ({ ...o, [motivo]: !open }))}>
              <span className="text-sm font-semibold text-slate-900">
                {open ? <ChevronDown className="w-3 h-3 inline"/> : <ChevronRight className="w-3 h-3 inline"/>}
                {' '}{MOTIVOS_LABEL[motivo] || motivo}
              </span>
              <span className="text-xs text-slate-600">
                {filas.length} filas · {numCampos} campos · {clientesUnicos} clientes
              </span>
            </button>
            {open && <div className="px-3 py-2 divide-y">
              {filas.map((f, i) => <FilaResultado key={i} det={f}/>)}
            </div>}
          </div>;
        })}
      </div>
    </div>}

    <div>
      <p className="text-xs uppercase font-bold text-slate-500 mb-2">Detalle por cliente</p>
      <div className="space-y-1">
        {Object.entries(clientes).map(([cliente, filas]) => {
          const aplic = filas.filter(f => f.resultado_fila === 'APLICADA').length;
          const fall = filas.length - aplic;
          const campos_ko = filas.reduce((n, f) => n + (f.campos || []).filter(c => c.resultado !== 'APLICADA' && c.resultado !== 'INFORMATIVA').length, 0);
          const motivos_principales = Array.from(new Set(filas.filter(f => f.motivo_grupo).map(f => MOTIVOS_LABEL[f.motivo_grupo] || f.motivo_grupo))).slice(0, 3);
          const open = !!openCliente[cliente];
          return <div key={cliente} data-testid={`cliente-${cliente}`} className="border rounded-lg overflow-hidden">
            <button className="w-full text-left px-3 py-2 bg-white hover:bg-slate-50 flex items-center justify-between"
              onClick={() => setOpenCliente(o => ({ ...o, [cliente]: !open }))}>
              <span className="text-sm font-medium text-slate-900">
                {open ? <ChevronDown className="w-3 h-3 inline"/> : <ChevronRight className="w-3 h-3 inline"/>}
                {' '}<b>{cliente}</b>
                {motivos_principales.length > 0 && <span className="ml-2 text-xs text-slate-500">· {motivos_principales.join(' · ')}</span>}
              </span>
              <span className="text-xs">
                <span className="text-emerald-700 font-bold">{aplic}</span>
                <span className="text-slate-400"> / </span>
                <span className={fall ? 'text-red-700 font-bold' : 'text-slate-400'}>{fall}</span>
                <span className="text-slate-500"> · {campos_ko} campos KO</span>
              </span>
            </button>
            {open && <div className="px-3 py-2 space-y-2">
              {filas.map((f, i) => <FilaResultado key={i} det={f}/>)}
            </div>}
          </div>;
        })}
      </div>
    </div>
  </div>;
}


function FilaResultado({ det }) {
  const [open, setOpen] = React.useState(false);
  const tono = det.resultado_fila === 'APLICADA' ? 'bg-emerald-50 border-emerald-200'
    : det.resultado_fila === 'FALLIDA_POSTVERIFICACION' ? 'bg-red-100 border-red-400'
    : det.resultado_fila === 'CONFLICTO' ? 'bg-amber-50 border-amber-300'
    : 'bg-red-50 border-red-200';
  return <div data-testid={`res-fila-${det.suscripcion_id}`} className={`border rounded-lg text-xs ${tono}`}>
    <button className="w-full text-left px-2 py-1.5 flex items-center justify-between"
      onClick={() => setOpen(o => !o)}>
      <span>
        {open ? <ChevronDown className="w-3 h-3 inline"/> : <ChevronRight className="w-3 h-3 inline"/>}
        {' '}<b>{det.matricula || '—'}</b>
        <span className="text-slate-500 ml-2">{det.cliente_nombre}</span>
        <span className="ml-2 px-1.5 rounded-full bg-white border font-bold uppercase text-[9px]">{det.resultado_fila}</span>
      </span>
      <span className="text-[10px] text-slate-500 font-mono">{det.suscripcion_id?.slice(0, 8)}…</span>
    </button>
    {det.motivo && <p className="px-2 pb-1 text-slate-700 italic">{det.motivo}</p>}
    {open && <div className="border-t px-2 py-1.5 space-y-1">
      {(det.campos || []).map((c, j) => {
        const aplicado = c.resultado === 'APLICADA';
        return <div key={j} className="mt-1" data-testid={`res-campo-${det.suscripcion_id}-${c.campo}`}>
          <b>{c.campo}</b>: <code className="bg-white/70 px-1 rounded">{c.valor_anterior || '∅'}</code>
          {' '}→ <code className="bg-white/70 px-1 rounded">{c.valor_solicitado || '∅'}</code>
          {' '}· <span className={aplicado ? 'text-emerald-800 font-bold' : c.resultado === 'INFORMATIVA' ? 'text-blue-800' : c.resultado === 'FALLIDA_POSTVERIFICACION' ? 'text-red-900 font-bold' : 'text-red-800'}>{c.resultado}</span>
          {aplicado && c.valor_guardado !== undefined && <span className="text-emerald-800"> · Guardado: <code>{String(c.valor_guardado)}</code></span>}
          {!aplicado && c.valor_actual_bbdd !== undefined && <span className="text-slate-700"> · Valor actual BBDD: <code>{String(c.valor_actual_bbdd)}</code></span>}
          {c.motivo && <span className="text-slate-600 italic"> · {c.motivo}</span>}
        </div>;
      })}
      {(det.consecuencias || []).length > 0 && <div className="border-t border-purple-200 mt-1 pt-1">
        <p className="text-[10px] uppercase font-bold text-purple-700">Consecuencias automáticas</p>
        {det.consecuencias.map((cn, k) => {
          const tono2 = cn.resultado === 'APLICADA' ? 'text-emerald-800 font-bold'
            : cn.resultado === 'FALLIDA_POSTVERIFICACION' ? 'text-red-900 font-bold'
            : 'text-purple-800';
          return <div key={k} className="text-purple-900 mt-0.5" data-testid={`res-cons-${det.suscripcion_id}-${cn.tipo}`}>
            · <b>[{cn.tipo}]</b> {cn.descripcion} · <span className={tono2}>{cn.resultado || '…'}</span>
            {cn.resultado === 'APLICADA' && cn.valor_guardado_post !== undefined && <span className="text-emerald-700"> · Guardado: <code>{String(cn.valor_guardado_post)}</code></span>}
          </div>;
        })}
      </div>}
    </div>}
  </div>;
}

// Iter52.61 · 2 · Barra de progreso de las operaciones en segundo plano.
function BarraProgreso({ etiqueta, prog, testid }) {
  const hechas = prog?.hechas || 0;
  const total = prog?.total || 0;
  const pct = total ? Math.min(100, Math.round((hechas / total) * 100)) : 10;
  return (
    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <p className="text-sm font-bold text-slate-700 mb-2">
        {etiqueta} <span className="tabular-nums">{hechas}</span>
        {total ? <> / <span className="tabular-nums">{total}</span></> : null}
      </p>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-600 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        Puedes cerrar la pantalla: el proceso sigue y al volver verás el estado.
      </p>
    </div>
  );
}
